import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { Logo } from "./Logo";
import { SectionLink, useSectionNav, scrollToId } from "./SectionLink";
import { NAV_LINKS, SERVICES, PHONE_DISPLAY, PHONE_HREF } from "./data";

const navCls = "group relative font-body text-sm font-medium text-brand-navy transition-colors hover:text-brand-blue";

const useQuoteNav = () => {
  const go = useSectionNav();
  return (e) => {
    if (document.getElementById("quote")) {
      e.preventDefault();
      scrollToId("quote");
    } else go(e, "#contact");
  };
};

const ServicesMenu = () => {
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const ref = useRef(null);
  const open = hover || pinned;
  const close = () => { setHover(false); setPinned(false); };

  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setPinned(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [pinned]);

  return (
    <div ref={ref} className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} onKeyDown={(e) => e.key === "Escape" && close()}>
      <div className="flex items-center gap-0.5">
        <SectionLink hash="#services" data-testid="nav-link-services" className={navCls} onClick={close}>Services</SectionLink>
        <button type="button" aria-label="Show service pages" aria-expanded={open} onClick={() => setPinned((v) => !v)} data-testid="nav-services-dropdown-toggle" className="rounded p-1 text-brand-navy hover:text-brand-blue">
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="absolute left-1/2 top-full z-50 w-[340px] -translate-x-1/2 pt-4" data-testid="nav-services-dropdown">
          <ul className="soft-card p-2 shadow-lg">
            {SERVICES.map(({ id, slug, title, icon: Icon }) => (
              <li key={id}>
                <Link to={`/services/${slug}`} onClick={close} data-testid={`nav-service-link-${id}`} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-brand-navy transition-colors hover:bg-brand-offwhite hover:text-brand-blue">
                  <Icon className="h-4 w-4 shrink-0 text-brand-blue" strokeWidth={1.75} /> {title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const close = () => setOpen(false);
  const onQuote = useQuoteNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 border-b bg-white transition-shadow duration-300 ${scrolled ? "border-brand-surface shadow-sm" : "border-transparent"}`}>
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <SectionLink hash="#home" data-testid="header-logo-link" onClick={close} aria-label="JMS Technologies home">
          <Logo compact className="h-11" testId="header-logo" />
        </SectionLink>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.label === "Services" ? (
              <ServicesMenu key={l.href} />
            ) : (
              <SectionLink key={l.href} hash={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className={navCls}>
                {l.label}
              </SectionLink>
            )
          )}
        </nav>
        <div className="hidden items-center gap-5 lg:flex">
          <a href={PHONE_HREF} data-testid="header-phone-link" className="hidden items-center gap-2 text-sm font-medium text-brand-navy hover:text-brand-blue xl:flex">
            <Phone className="h-4 w-4 text-brand-blue" strokeWidth={1.75} /> {PHONE_DISPLAY}
          </a>
          <a href="/#contact" onClick={onQuote} data-testid="header-get-quote-button" className="btn-primary py-2.5">
            Request a Quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <button type="button" data-testid="mobile-menu-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((v) => !v)} className="rounded-lg p-2 text-brand-navy transition-colors hover:bg-brand-offwhite lg:hidden">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && <MobileMenu onNavigate={close} />}
    </header>
  );
};

const MobileServiceLinks = ({ onNavigate }) => (
  <ul className="border-b border-brand-surface pb-2 pl-3">
    {SERVICES.map(({ id, slug, title, icon: Icon }) => (
      <li key={id}>
        <Link to={`/services/${slug}`} onClick={onNavigate} data-testid={`mobile-service-link-${id}`} className="flex items-center gap-2.5 py-2 text-sm text-brand-slate hover:text-brand-blue">
          <Icon className="h-4 w-4 shrink-0 text-brand-blue" strokeWidth={1.75} /> {title}
        </Link>
      </li>
    ))}
  </ul>
);

const MobileMenu = ({ onNavigate }) => {
  const onQuote = useQuoteNav();
  return (
    <div data-testid="mobile-menu" className="max-h-[calc(100vh-72px)] overflow-y-auto border-t border-brand-surface bg-white px-5 pb-6 pt-2 shadow-lg lg:hidden">
      <nav className="flex flex-col" aria-label="Mobile">
        {NAV_LINKS.map((l) => (
          <div key={l.href}>
            <SectionLink hash={l.href} onClick={onNavigate} data-testid={`mobile-nav-link-${l.label.toLowerCase()}`} className="block border-b border-brand-surface py-3.5 text-base font-medium text-brand-navy">
              {l.label}
            </SectionLink>
            {l.label === "Services" && <MobileServiceLinks onNavigate={onNavigate} />}
          </div>
        ))}
        <Link to="/areas" onClick={onNavigate} data-testid="mobile-nav-link-areas" className="block border-b border-brand-surface py-3.5 text-base font-medium text-brand-navy">
          Service Areas
        </Link>
      </nav>
      <div className="mt-5 flex flex-col gap-3">
        <a href="/#contact" onClick={(e) => { onNavigate(); onQuote(e); }} data-testid="mobile-get-quote-button" className="btn-primary w-full">
          Request a Quote <ArrowRight className="h-4 w-4" />
        </a>
        <a href={PHONE_HREF} data-testid="mobile-phone-link" className="btn-secondary w-full">
          <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
};
